import './styles.css';

import {
  clearAuth,
  consumeHandoffFragment,
  handoffUrl,
  loadStoredAuth,
  type TxxtAuth,
} from './auth/txxt';
import { DEFAULT_SETTINGS, SHARE_MAX_BYTES, type OutputEdge } from './constants';
import { convertStaticImage } from './conversion/static';
import { AnimatedGifConverter, type AnimatedConversionResult } from './gif/converter';
import { enforceAnimatedGifLimits } from './gif/limits';
import type { GifProgressPhase } from './gif/protocol';
import { BlobUrlSlot } from './lib/blob-url';
import { ConversionError, messageFromUnknown } from './lib/errors';
import { validateSourceFile, type SupportedFormat } from './lib/file-validation';
import { outputFilename } from './lib/filename';
import { formatBytes, formatSizeChange } from './lib/format';
import { parseGifMetadata, type GifMetadata } from './lib/gif-metadata';
import {
  loadRecentShares,
  loadSettings,
  removeRecentShare,
  saveRecentShare,
  saveSettings,
  type RecentShare,
} from './lib/storage';
import { createDeleteToken, deleteShare, uploadShare, type ShareResponse } from './sharing/client';

function element<T extends HTMLElement>(id: string): T {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing required element #${id}`);
  return value as T;
}

const fileInput = element<HTMLInputElement>('file-input');
const dropZone = element<HTMLDivElement>('drop-zone');
const fileSummary = element<HTMLDivElement>('file-summary');
const fileName = element<HTMLParagraphElement>('file-name');
const fileDetails = element<HTMLParagraphElement>('file-details');
const removeFileButton = element<HTMLButtonElement>('remove-file');
const controls = element<HTMLFormElement>('controls');
const qualityInput = element<HTMLInputElement>('quality');
const qualityValue = element<HTMLOutputElement>('quality-value');
const outputEdgeSelect = element<HTMLSelectElement>('output-edge');
const gifSettings = element<HTMLFieldSetElement>('gif-settings');
const gifProfileSelect = element<HTMLSelectElement>('gif-profile');
const convertButton = element<HTMLButtonElement>('convert');
const cancelButton = element<HTMLButtonElement>('cancel');
const statusPanel = element<HTMLDivElement>('status-panel');
const statusLabel = element<HTMLSpanElement>('status-label');
const statusPercent = element<HTMLSpanElement>('status-percent');
const progress = element<HTMLProgressElement>('progress');
const errorPanel = element<HTMLDivElement>('error-panel');
const errorMessage = element<HTMLParagraphElement>('error-message');
const retryActions = element<HTMLDivElement>('retry-actions');
const resultSection = element<HTMLElement>('result');
const resultKind = element<HTMLSpanElement>('result-kind');
const preview = element<HTMLImageElement>('preview');
const metrics = element<HTMLDListElement>('metrics');
const download = element<HTMLAnchorElement>('download');
const shareLimitNote = element<HTMLParagraphElement>('share-limit-note');
const shareButton = element<HTMLButtonElement>('share');
const turnstileWidget = element<HTMLDivElement>('turnstile-widget');
const shareResult = element<HTMLDivElement>('share-result');
const shareUrl = element<HTMLInputElement>('share-url');
const copyShareButton = element<HTMLButtonElement>('copy-share');
const shareExpiry = element<HTMLParagraphElement>('share-expiry');
const recentSection = element<HTMLElement>('recent-section');
const recentList = element<HTMLUListElement>('recent-list');
const authArea = element<HTMLDivElement>('auth');
const authSignedOut = element<HTMLDivElement>('auth-signed-out');
const authSignedIn = element<HTMLDivElement>('auth-signed-in');
const authSignInButton = element<HTMLButtonElement>('auth-signin');
const authSignOutButton = element<HTMLButtonElement>('auth-signout');
const authEmail = element<HTMLSpanElement>('auth-email');

type SelectedFile = {
  file: File;
  format: SupportedFormat;
  gif?: GifMetadata;
};

type ConversionResult = {
  blob: Blob;
  sourceName: string;
  sourceBytes: number;
  sourceWidth: number;
  sourceHeight: number;
  outputWidth: number;
  outputHeight: number;
  animated: boolean;
  sourceFrames?: number;
  outputFrames?: number;
  durationMs?: number;
};

type PendingShare = { idempotencyKey: string; deleteToken: string };

const settings = loadSettings();
qualityInput.value = String(settings.quality);
qualityValue.value = String(settings.quality);
outputEdgeSelect.value = settings.outputEdge;
gifProfileSelect.value = settings.gifProfile;

const blobUrl = new BlobUrlSlot();
const animatedConverter = new AnimatedGifConverter();
let selected: SelectedFile | undefined;
let currentResult: ConversionResult | undefined;
let conversionAbort: AbortController | undefined;
let turnstileToken = '';
let turnstileId: string | undefined;
let pendingShare: PendingShare | undefined;
let sharing = false;

const apiOrigin = import.meta.env.VITE_API_ORIGIN?.trim() ?? '';
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? '';
const txxtOrigin = import.meta.env.VITE_TXXT_ORIGIN?.trim() ?? '';

function hide(elementToHide: HTMLElement, hidden: boolean): void {
  elementToHide.classList.toggle('hidden', hidden);
}

function setError(message: string, canvasRetry = false): void {
  errorMessage.textContent = message;
  hide(errorPanel, false);
  hide(retryActions, !canvasRetry);
  errorPanel.focus();
}

function clearError(): void {
  hide(errorPanel, true);
  hide(retryActions, true);
  errorMessage.textContent = '';
}

function persistSettings(): void {
  saveSettings({
    quality: Number(qualityInput.value),
    outputEdge: outputEdgeSelect.value as OutputEdge,
    gifProfile: gifProfileSelect.value as 'low' | 'medium',
  });
  if (selected && currentResult) convertButton.textContent = 'Reconvert';
}

function updateProgress(label: string, percent: number): void {
  const rounded = Math.max(0, Math.min(100, Math.round(percent)));
  statusLabel.textContent = label;
  statusPercent.textContent = `${rounded}%`;
  progress.value = rounded;
  progress.textContent = `${rounded}%`;
}

const phaseLabels: Record<GifProgressPhase, string> = {
  decode: 'Decoding GIF frames',
  resize: 'Resizing and aggregating frames',
  encode: 'Encoding animated WebP',
  finalize: 'Finalizing animation',
};

function setBusy(busy: boolean): void {
  controls.setAttribute('aria-busy', String(busy));
  convertButton.disabled = busy || !selected;
  hide(cancelButton, !busy);
  hide(statusPanel, !busy);
  fileInput.disabled = busy;
  removeFileButton.disabled = busy;
}

function resetTurnstile(): void {
  turnstileToken = '';
  if (turnstileId && window.turnstile) window.turnstile.reset(turnstileId);
  refreshShareAvailability();
}

function refreshShareAvailability(): void {
  if (!currentResult) {
    shareButton.disabled = true;
    return;
  }
  const tooLarge = currentResult.blob.size > SHARE_MAX_BYTES;
  const configured = Boolean(apiOrigin && turnstileSiteKey);
  shareButton.disabled = tooLarge || !configured || !turnstileToken || sharing;
  if (tooLarge) {
    shareLimitNote.textContent = 'This result is over 15 MiB. It can be downloaded but not shared.';
  } else if (!configured) {
    shareLimitNote.textContent =
      'Sharing is not configured in this build. Download remains available.';
  } else {
    shareLimitNote.textContent = 'Complete the verification to enable an expiring public link.';
  }
}

function metric(term: string, description: string): void {
  const item = document.createElement('div');
  const dt = document.createElement('dt');
  const dd = document.createElement('dd');
  dt.textContent = term;
  dd.textContent = description;
  item.append(dt, dd);
  metrics.append(item);
}

function commitResult(result: ConversionResult): void {
  currentResult = result;
  pendingShare = undefined;
  const url = blobUrl.replace(result.blob);
  preview.src = url;
  download.href = url;
  download.download = outputFilename(result.sourceName);
  resultKind.textContent = result.animated ? 'Animated' : 'Static';
  metrics.replaceChildren();
  metric('Dimensions', `${result.outputWidth} × ${result.outputHeight} px`);
  metric('Output', formatBytes(result.blob.size));
  metric('Source', formatBytes(result.sourceBytes));
  metric('Change', formatSizeChange(result.sourceBytes, result.blob.size));
  if (result.animated) {
    metric('Frames', `${result.sourceFrames ?? 0} source → ${result.outputFrames ?? 0} output`);
    metric('Duration', `${((result.durationMs ?? 0) / 1000).toFixed(2)} seconds`);
  }
  hide(resultSection, false);
  hide(shareResult, true);
  refreshShareAvailability();
  resultSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

async function selectFile(file: File): Promise<void> {
  clearError();
  try {
    const format = await validateSourceFile(file);
    let next: SelectedFile = { file, format };
    if (format === 'gif') {
      const gif = parseGifMetadata(new Uint8Array(await file.arrayBuffer()));
      if (gif.frameCount > 1) enforceAnimatedGifLimits(gif);
      next = { ...next, gif };
    }
    selected = next;
    fileName.textContent = file.name;
    const details = [format.toUpperCase(), formatBytes(file.size)];
    if (next.gif) {
      details.push(`${next.gif.width} × ${next.gif.height} px`);
      details.push(`${next.gif.frameCount} frame${next.gif.frameCount === 1 ? '' : 's'}`);
    }
    fileDetails.textContent = details.join(' · ');
    hide(fileSummary, false);
    hide(dropZone, true);
    hide(gifSettings, !(next.gif && next.gif.frameCount > 1));
    convertButton.disabled = false;
    convertButton.textContent = currentResult ? 'Reconvert' : 'Convert';
  } catch (error) {
    fileInput.value = '';
    setError(messageFromUnknown(error));
  }
}

function clearSelection(): void {
  conversionAbort?.abort();
  animatedConverter.cancel();
  selected = undefined;
  currentResult = undefined;
  pendingShare = undefined;
  fileInput.value = '';
  preview.removeAttribute('src');
  blobUrl.clear();
  hide(fileSummary, true);
  hide(dropZone, false);
  hide(gifSettings, true);
  hide(resultSection, true);
  hide(shareResult, true);
  convertButton.disabled = true;
  convertButton.textContent = 'Convert';
  clearError();
}

async function runConversion(edgeOverride?: number): Promise<void> {
  if (!selected) return;
  clearError();
  conversionAbort?.abort();
  animatedConverter.cancel();
  conversionAbort = new AbortController();
  setBusy(true);
  updateProgress('Preparing image', 4);

  try {
    if (selected.gif && selected.gif.frameCount > 1) {
      const animated: AnimatedConversionResult = await animatedConverter.convert(
        selected.file,
        gifProfileSelect.value as 'low' | 'medium',
        (phase, percent) => updateProgress(phaseLabels[phase], percent),
        conversionAbort.signal,
      );
      commitResult({
        blob: animated.blob,
        sourceName: selected.file.name,
        sourceBytes: selected.file.size,
        sourceWidth: animated.sourceDimensions.width,
        sourceHeight: animated.sourceDimensions.height,
        outputWidth: animated.outputDimensions.width,
        outputHeight: animated.outputDimensions.height,
        animated: animated.animated,
        sourceFrames: animated.sourceFrames,
        outputFrames: animated.outputFrames,
        durationMs: animated.durationMs,
      });
    } else {
      updateProgress('Decoding image', 20);
      const requestedEdge = edgeOverride ?? outputEdgeSelect.value;
      const staticResult = await convertStaticImage(selected.file, {
        quality: Number(qualityInput.value),
        longestEdge: requestedEdge === 'original' ? 'original' : Number(requestedEdge),
        signal: conversionAbort.signal,
      });
      updateProgress('Finalizing WebP', 92);
      commitResult({
        blob: staticResult.blob,
        sourceName: selected.file.name,
        sourceBytes: selected.file.size,
        sourceWidth: staticResult.sourceDimensions.width,
        sourceHeight: staticResult.sourceDimensions.height,
        outputWidth: staticResult.outputDimensions.width,
        outputHeight: staticResult.outputDimensions.height,
        animated: false,
      });
    }
    updateProgress('Complete', 100);
    convertButton.textContent = 'Reconvert';
  } catch (error) {
    if (!(error instanceof ConversionError && error.code === 'cancelled')) {
      setError(
        messageFromUnknown(error),
        error instanceof ConversionError && error.code === 'canvas-allocation',
      );
    }
  } finally {
    conversionAbort = undefined;
    setBusy(false);
  }
}

function renderShare(share: RecentShare): void {
  shareUrl.value = share.url;
  shareExpiry.textContent = `Expires 7 days after the last view. Next expiry: ${new Date(share.idleExpiresAt).toLocaleString()}.`;
  hide(shareResult, false);
}

function renderRecentShares(): void {
  const shares = loadRecentShares();
  recentList.replaceChildren();
  hide(recentSection, shares.length === 0);
  for (const share of shares) {
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = share.url;
    link.target = '_blank';
    link.rel = 'noreferrer';
    link.textContent = share.url;
    const expiry = document.createElement('span');
    expiry.textContent = `Expires ${new Date(share.idleExpiresAt).toLocaleDateString()} unless viewed again`;
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.textContent = 'Delete share';
    remove.addEventListener('click', () => {
      remove.disabled = true;
      void deleteShare(share.url, share.deleteToken)
        .then(() => {
          removeRecentShare(share.url);
          renderRecentShares();
        })
        .catch((error: unknown) => {
          remove.disabled = false;
          setError(messageFromUnknown(error));
        });
    });
    item.append(link, expiry, remove);
    recentList.append(item);
  }
}

async function createShare(): Promise<void> {
  if (!currentResult || !turnstileToken || !apiOrigin) return;
  sharing = true;
  refreshShareAvailability();
  shareButton.textContent = 'Uploading…';
  pendingShare ??= { idempotencyKey: crypto.randomUUID(), deleteToken: createDeleteToken() };

  try {
    const response: ShareResponse = await uploadShare(
      apiOrigin,
      currentResult.blob,
      turnstileToken,
      pendingShare.idempotencyKey,
      pendingShare.deleteToken,
    );
    const record: RecentShare = { ...response, deleteToken: pendingShare.deleteToken };
    saveRecentShare(record);
    renderShare(record);
    renderRecentShares();
    pendingShare = undefined;
  } catch (error) {
    setError(messageFromUnknown(error));
  } finally {
    sharing = false;
    shareButton.textContent = 'Upload & share';
    resetTurnstile();
  }
}

function initializeTurnstile(attempt = 0): void {
  if (!turnstileSiteKey) {
    refreshShareAvailability();
    return;
  }
  if (!window.turnstile) {
    if (attempt < 40) window.setTimeout(() => initializeTurnstile(attempt + 1), 250);
    return;
  }
  if (turnstileId) return;
  turnstileId = window.turnstile.render(turnstileWidget, {
    sitekey: turnstileSiteKey,
    action: 'upload',
    callback: (token) => {
      turnstileToken = token;
      refreshShareAvailability();
    },
    'expired-callback': () => resetTurnstile(),
    'error-callback': () => {
      turnstileToken = '';
      refreshShareAvailability();
    },
  });
}

function renderAuth(auth: TxxtAuth | undefined): void {
  if (!txxtOrigin) {
    hide(authArea, true);
    return;
  }
  hide(authArea, false);
  hide(authSignedOut, Boolean(auth));
  hide(authSignedIn, !auth);
  authEmail.textContent = auth?.email ?? '';
}

authSignInButton.addEventListener('click', () => {
  window.location.assign(handoffUrl(txxtOrigin, `${window.location.origin}/`));
});
authSignOutButton.addEventListener('click', () => {
  clearAuth();
  renderAuth(undefined);
});

fileInput.addEventListener('change', () => {
  const file = fileInput.files?.[0];
  if (file) void selectFile(file);
});
removeFileButton.addEventListener('click', clearSelection);
controls.addEventListener('submit', (event) => {
  event.preventDefault();
  void runConversion();
});
cancelButton.addEventListener('click', () => conversionAbort?.abort());
qualityInput.addEventListener('input', () => {
  qualityValue.value = qualityInput.value;
  persistSettings();
});
outputEdgeSelect.addEventListener('change', persistSettings);
gifProfileSelect.addEventListener('change', persistSettings);
retryActions.addEventListener('click', (event) => {
  const button = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-retry-edge]');
  if (button?.dataset.retryEdge) void runConversion(Number(button.dataset.retryEdge));
});

for (const eventName of ['dragenter', 'dragover']) {
  dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropZone.classList.add('dragging');
  });
}
for (const eventName of ['dragleave', 'drop']) {
  dropZone.addEventListener(eventName, (event) => {
    event.preventDefault();
    dropZone.classList.remove('dragging');
  });
}
dropZone.addEventListener('drop', (event) => {
  const files = event.dataTransfer?.files;
  if (!files?.length) return;
  if (files.length > 1) {
    setError('Drop one file at a time.');
    return;
  }
  const file = files[0];
  if (file) void selectFile(file);
});

shareButton.addEventListener('click', () => void createShare());
copyShareButton.addEventListener('click', () => {
  void navigator.clipboard.writeText(shareUrl.value).then(() => {
    copyShareButton.textContent = 'Copied';
    window.setTimeout(() => {
      copyShareButton.textContent = 'Copy';
    }, 1500);
  });
});

window.addEventListener('beforeunload', () => {
  conversionAbort?.abort();
  animatedConverter.cancel();
  blobUrl.clear();
});

if (!Number.isInteger(settings.quality)) {
  qualityInput.value = String(DEFAULT_SETTINGS.quality);
}
renderRecentShares();
refreshShareAvailability();
initializeTurnstile();
renderAuth(txxtOrigin ? (consumeHandoffFragment() ?? loadStoredAuth()) : undefined);
